/* fye-finish.js — the plain-ring finish picker.

   Its markup is snippets/fye-buybox-finish.liquid; read that file's header
   for the decisions. This file only carries the behaviour.

   NOT part of fye-ui.js, on purpose. fye-ui.js is 140KB and loads on every
   page; this is ~3KB and is wanted on plain wedding ring pages only, so the
   snippet loads it itself with defer. If a second section ever renders a
   finish picker, that is still true.

   Contract with the markup, all data attributes, no classes as hooks except
   the `is-on` / `hidden` states the theme already uses elsewhere:

     [data-fye-finish]            root
     [data-fye-finish-set]        "no" | "yes" segmented buttons
     [data-fye-finish-pick]       the chosen-finish row in the buy box
     [data-fye-finish-prop]       hidden input, name="properties[Finish]"
     [data-fye-finish-modal]      the modal shell
     [data-fye-ftile]             a tile, carrying data-name/-prop/-hm/-search

   Two states, deliberately separate:

     applied   what the buy box shows and what the cart will receive
     draft     what is highlighted inside the modal

   Cancel and Escape throw the draft away. Confirm promotes it. That is the
   whole reason they are not one variable.

   THE PDX LAYOUT (27/09/2026, CRO redesign, behind the preview gate) adds:

     [data-fye-finish-quick="<prop>"]  a quick-pick card; a tap APPLIES the
                                       tile whose data-prop matches, no modal
     [data-fye-finish-more]            the fourth card; shows a picked finish
                                       that is not one of the quick three
     [data-pdx-finish-price]           "Standard" or "+£50" beside the name
     [data-fye-finish-cat="<cat>"]     category chips in the picker, working
                                       with the search, never instead of it
     [data-pdx-finish-total]           the ring total for the draft finish,
                                       from the price fye-ui.js last painted

   All of it is absent from the old layout, and every lookup below tolerates
   that, so the old page behaves exactly as it did.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-fye-finish]');
  if (!root) return;

  var POLISHED = {
    prop: 'Polished',
    name: 'Polished',
    hm: '',
    fee: '0',
    feeVariant: '',
    feeLabel: '',
    isPolished: true,
    meta: 'Standard finish',
    draftMeta: 'The standard finish, no extra work'
  };

  var q = function (sel, scope) { return (scope || root).querySelector(sel); };
  var all = function (sel, scope) { return Array.prototype.slice.call((scope || root).querySelectorAll(sel)); };

  var modal = q('[data-fye-finish-modal]');
  var panel = q('[data-fye-finish-panel]');
  var pick = q('[data-fye-finish-pick]');
  var prop = q('[data-fye-finish-prop]');
  var hint = q('[data-fye-finish-hint]');
  var reset = q('[data-fye-finish-reset]');
  var search = q('[data-fye-finish-search]');
  var count = q('[data-fye-finish-count]');
  var tiles = all('[data-fye-ftile]');

  var out = {
    thumb: q('[data-fye-finish-thumb]'),
    name: q('[data-fye-finish-name]'),
    meta: q('[data-fye-finish-meta]')
  };
  var foot = {
    thumb: q('[data-fye-finish-draftimg]'),
    name: q('[data-fye-finish-draftname]'),
    meta: q('[data-fye-finish-draftmeta]')
  };

  var quicks = all('[data-fye-finish-quick]');
  var more = q('[data-fye-finish-more]');
  var priceOut = q('[data-pdx-finish-price]');
  var totalOut = q('[data-pdx-finish-total]');
  var cats = all('[data-fye-finish-cat]');
  var cat = 'all';

  var applied = tiles[0] || null;   /* the Polished tile */
  var draft = applied;
  var lastFocus = null;

  /* ---- reading a tile ---------------------------------------------------
     A tile is the source of truth for its own copy, so the property string
     is composed once, in Liquid, where the HM number lives. Nothing here
     rebuilds "Name (HM 38)" from parts. */

  function read(tile) {
    if (!tile) return POLISHED;
    var img = tile.querySelector('img');
    return {
      prop: tile.getAttribute('data-prop') || POLISHED.prop,
      name: tile.getAttribute('data-name') || POLISHED.name,
      hm: tile.getAttribute('data-hm') || '',
      fee: tile.getAttribute('data-fee') || '0',
      feeVariant: tile.getAttribute('data-fee-variant') || '',
      feeLabel: tile.getAttribute('data-fee-label') || '',
      src: img ? img.getAttribute('src') : '',
      isPolished: tile === tiles[0]
    };
  }

  /* "HM 38 · +£50" — the order reference a shopper can quote us, then what
     it adds. Polished says what it is and carries no price at all. */
  function metaOf(f) {
    if (f.isPolished) return POLISHED.meta;
    return f.feeLabel ? f.hm + ' · ' + f.feeLabel : f.hm;
  }

  /* ---- painting --------------------------------------------------------- */

  function paintApplied() {
    var f = read(applied);
    if (prop) prop.value = f.prop;
    if (out.name) out.name.textContent = f.name;
    if (out.meta) out.meta.textContent = metaOf(f);
    if (out.thumb && f.src) out.thumb.src = f.src;
    if (reset) reset.hidden = f.isPolished;
    if (priceOut) priceOut.textContent = f.isPolished ? 'Standard' : (f.feeLabel || '');

    /* Quick cards, and the fourth card when the finish is not one of them. */
    var quickHit = false;
    quicks.forEach(function (c) {
      var on = c.getAttribute('data-fye-finish-quick') === f.prop;
      if (on) quickHit = true;
      c.classList.toggle('is-on', on);
      c.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    if (more) {
      var moreName = more.querySelector('[data-fye-finish-more-name]');
      var moreMeta = more.querySelector('[data-fye-finish-more-meta]');
      var custom = !quickHit && !f.isPolished;
      more.classList.toggle('is-on', custom);
      if (moreName) moreName.textContent = custom ? f.name : 'View more finishes';
      if (moreMeta) moreMeta.textContent = custom ? (f.feeLabel + ' · Change') : (moreMeta.getAttribute('data-default') || '');
    }

    /* The uplift travels on the root, which is where fye-ui.js reads it for
       the selected price and for the cart line — the same two attributes
       engraving uses. Written in one place, so the price the shopper is
       shown and the line they are charged cannot drift apart. */
    root.setAttribute('data-fee-price', f.isPolished ? '0' : f.fee);
    root.setAttribute('data-fee-variant', f.isPolished ? '' : f.feeVariant);

    /* fye-ui.js owns the displayed price and has already handled this click
       by the time we run, so it has to be asked to paint again. */
    if (window.FYE && window.FYE.refresh) window.FYE.refresh(root);
  }

  function paintDraft() {
    var f = read(draft);
    if (foot.name) foot.name.textContent = f.name;
    if (foot.meta) foot.meta.textContent = f.isPolished ? POLISHED.draftMeta : metaOf(f);
    if (foot.thumb && f.src) foot.thumb.src = f.src;

    tiles.forEach(function (t) {
      var on = t === draft;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    /* Ring total for the draft: the total fye-ui.js last painted, less the
       applied finish's fee, plus the draft's. Read, never recomputed. */
    if (totalOut) {
      var form = root.closest('form');
      var last = form && form.__pdxPriced;
      if (last) {
        var sum = last.total - (parseInt(read(applied).fee, 10) || 0) + (parseInt(f.fee, 10) || 0);
        totalOut.textContent = 'Ring total ' + money(sum);
      }
    }
  }

  function money(pennies) {
    return '£' + (pennies / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ---- the toggle -------------------------------------------------------
     Yes opens the picker immediately: the toggle is the way in, not a switch
     with nothing behind it. No returns the ring to polished outright, so a
     shopper who changes their mind does not leave a finish on the order. */

  function setToggle(on) {
    all('[data-fye-finish-set]').forEach(function (b) {
      var isOn = (b.getAttribute('data-fye-finish-set') === 'yes') === on;
      b.classList.toggle('is-on', isOn);
      b.setAttribute('aria-checked', isOn ? 'true' : 'false');
    });
    if (pick) pick.hidden = !on;
    if (hint) hint.hidden = on;
  }

  /* ---- the modal --------------------------------------------------------
     `hidden` plus the root-element scroll lock, matching the diamond
     picker in fye-ui.js. Focus moves to
     the panel and returns to whatever opened it, because a modal that leaves
     the caret at the top of the document is unusable by keyboard. */

  function open() {
    if (!modal) return;
    lastFocus = document.activeElement;
    draft = applied;
    paintDraft();
    cat = 'all';
    if (cats.length) setCat('all');
    filter('');
    if (search) search.value = '';
    modal.hidden = false;
    /* This theme locks scroll on the root element, not with a body
       class: see openDrawer / openPicker in fye-ui.js. No such body
       class rule exists anywhere in v3, so the page would otherwise
       have carried on scrolling behind the modal. */
    document.documentElement.style.overflow = 'hidden';
    if (panel) panel.focus();
  }

  function close() {
    if (!modal) return;
    modal.hidden = true;
    /* Release only once nothing else is holding the lock, matching
       closeDrawer / closePicker in fye-ui.js. */
    if (!document.querySelector('[data-fye-picker]:not([hidden])') &&
        !document.querySelector('[data-fye-drawer].is-open')) {
      document.documentElement.style.overflow = '';
    }
    draft = applied;
    paintDraft();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---- search -----------------------------------------------------------
     Matches the customer name and the HM number, because a shopper on the
     phone to us will be reading the number back. Tiles are hidden rather
     than removed, so the grid does not reflow into a different order when
     the field is cleared. */

  function filter(term) {
    var t = String(term || '').trim().toLowerCase();
    var shown = 0;

    tiles.forEach(function (tile) {
      var hay = tile.getAttribute('data-search') || '';
      var inCat = cat === 'all' || tile.getAttribute('data-cat') === cat;
      var hit = inCat && (!t || hay.indexOf(t) > -1);
      tile.hidden = !hit;
      if (hit) shown++;
    });

    if (count) {
      if (cats.length) {
        count.textContent = shown + ' of ' + tiles.length + ' finishes';
      } else {
        count.textContent = t
          ? shown + (shown === 1 ? ' match' : ' matches')
          : tiles.length + ' finishes';
      }
    }
  }

  function setCat(next) {
    cat = next || 'all';
    cats.forEach(function (c) {
      var on = c.getAttribute('data-fye-finish-cat') === cat;
      c.classList.toggle('is-on', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    filter(search ? search.value : '');
  }

  /* ---- wiring ----------------------------------------------------------- */

  root.addEventListener('click', function (e) {
    var el;

    if ((el = e.target.closest('[data-fye-finish-set]'))) {
      var yes = el.getAttribute('data-fye-finish-set') === 'yes';
      setToggle(yes);
      if (yes) {
        open();
      } else {
        applied = tiles[0];
        paintApplied();
        close();
      }
      return;
    }

    if ((el = e.target.closest('[data-fye-finish-quick]'))) {
      var want = el.getAttribute('data-fye-finish-quick');
      var hitTile = null;
      tiles.forEach(function (t) { if (t.getAttribute('data-prop') === want) hitTile = t; });
      if (hitTile) {
        applied = hitTile;
        draft = hitTile;
        paintApplied();
        paintDraft();
      }
      return;
    }

    if ((el = e.target.closest('[data-fye-finish-cat]'))) {
      setCat(el.getAttribute('data-fye-finish-cat'));
      return;
    }

    if (e.target.closest('[data-fye-finish-open]')) { open(); return; }
    if (e.target.closest('[data-fye-finish-close]')) { close(); return; }
    if (e.target.closest('[data-fye-finish-cancel]')) { close(); return; }

    if (e.target.closest('[data-fye-finish-reset]')) {
      applied = tiles[0];
      paintApplied();
      return;
    }

    if ((el = e.target.closest('[data-fye-ftile]'))) {
      draft = el;
      paintDraft();
      return;
    }

    if (e.target.closest('[data-fye-finish-confirm]')) {
      applied = draft;
      paintApplied();
      close();
    }
  });

  if (search) {
    search.addEventListener('input', function () { filter(search.value); });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal && !modal.hidden) close();
  });

  /* ---- the wishlist ------------------------------------------------------
     A saved ring is restored through FYE.readConfig / FYE.applyConfig, which
     live in fye-ui.js and know nothing about this picker. So the picker
     publishes its own read and apply, and fye-ui.js calls them only if they
     are there: the finish comes back with the ring, and a page with no
     picker is untouched.

     Apply deliberately does NOT click Yes. Yes opens the modal, and a ring
     restored from the wishlist should not greet the shopper with a dialog. */

  window.FYE = window.FYE || {};
  window.FYE.finishState = {
    read: function () {
      return { prop: applied ? applied.getAttribute('data-prop') || '' : '' };
    },

    apply: function (state) {
      if (!state || !state.prop) return;

      var found = null;
      tiles.forEach(function (t) {
        if (t.getAttribute('data-prop') === state.prop) found = t;
      });
      if (!found) return;

      applied = found;
      draft = found;
      setToggle(!read(found).isPolished);
      paintApplied();
      paintDraft();
    }
  };

  setToggle(false);
  paintApplied();
  paintDraft();
}());
